/**
 * BowtieVisual - Funil Bowtie do TITAM AI
 * Exibe VMs (Volume Metrics) sequenciais com CRs (taxas de conversão).
 * Design pattern: funil horizontal com hierarquia visual decrescente.
 */

import Icon from './icons.jsx'
import { useViewMode } from '../hooks/useViewMode.js'

// Dados mock para visualização inicial
const MOCK_DATA = {
  period: { start: '2026-05-01', end: '2026-05-13', label: 'Últimos 13 dias' },
  comparison_period: { start: '2026-04-18', end: '2026-04-30', label: 'Período anterior' },
  vms: {
    vm0: {
      label: { simple: 'Impressões', advanced: 'Impressões (VM0)' },
      current: 1607800, previous: 1414864, change: 14.2
    },
    vm1: {
      label: { simple: 'Visitas', advanced: 'Visitas (VM1)' },
      current: 45678, previous: 38065, change: 20.0
    },
    vm2: {
      label: { simple: 'Leads', advanced: 'Leads (VM2)' },
      current: 2405, previous: 1808, change: 33.0
    },
    vm3: {
      label: { simple: 'Lead qualificado', advanced: 'Qualificados (VM3)' },
      current: 843, previous: 720, change: 17.1
    },
    vm5: {
      label: { simple: 'Vendas', advanced: 'Vendas (VM5)' },
      current: 103, previous: 95, change: 8.4
    }
  },
  crs: {
    cr0: { current: 2.84, change: 5.3 },
    cr1: { current: 5.26, change: 10.7 },
    cr2: { current: 35.05, change: -12.1 },
    cr4: { current: 12.22, change: -1.9 }
  },
  sources: [
    { name: 'Meta Ads', visits: 34258, percentage: 75, cost: 33761, cpl: 18.70 },
    { name: 'Google Ads', visits: 4568, percentage: 10, cost: 7234, cpl: 30.10 },
    { name: 'ChatGPT', visits: 3654, percentage: 8, cost: 4823, cpl: 25.06 },
    { name: 'Direto', visits: 3198, percentage: 7, cost: 0, cpl: 0 }
  ],
  devices: [
    { name: 'Android', percentage: 42 },
    { name: 'iPhone', percentage: 28 },
    { name: 'Windows', percentage: 22 },
    { name: 'Linux', percentage: 8 }
  ],
  avg_time_to_sale_days: 12
}

function formatNumber(num) {
  if (num >= 1000) return num.toLocaleString('pt-BR')
  return String(num)
}

function formatPercent(num) {
  return num.toFixed(2).replace('.', ',') + '%'
}

function resolveLabel(label, isAdvanced) {
  if (label && typeof label === 'object') {
    return isAdvanced ? (label.advanced ?? label.simple) : label.simple
  }
  return label
}

function JourneyCol({ vm, cr, crName, isAdvanced }) {
  const { label, current } = vm
  return (
    <div className="bw-fcol" role="group" aria-label={resolveLabel(label, isAdvanced)}>
      <span className="bw-fcol-label">{resolveLabel(label, isAdvanced)}</span>
      <span className="bw-fcol-value">{formatNumber(current)}</span>
      <span className="bw-fcol-sub">
        {cr
          ? (isAdvanced ? `${crName} ${formatPercent(cr.current)}` : `conv. ${formatPercent(cr.current)}`)
          : ' '}
      </span>
    </div>
  )
}

export default function BowtieVisual({ data, title }) {
  // Fallback para o mock interno se nenhum data for passado (compat com chamadas antigas).
  const source = data || MOCK_DATA
  const { vms, crs, avg_time_to_sale_days } = source
  const { isAdvanced } = useViewMode()

  return (
    <section className="bw-root">
      <header className="bw-header">
        <div>
          <div className="bw-eyebrow">
            {title || (isAdvanced ? 'Funil Bowtie — VM0 → VM5' : 'Jornada do cliente')}
          </div>
        </div>
        <div className="bw-meta-item bw-meta-muted">
          <Icon n="clock" s={14} />
          <span>Tempo médio de venda: <strong>{avg_time_to_sale_days} dias</strong></span>
        </div>
      </header>

      <div className="bw-journey">
        <JourneyCol vm={vms.vm0} isAdvanced={isAdvanced} />
        <JourneyCol vm={vms.vm1} cr={crs.cr0} crName="CR0" isAdvanced={isAdvanced} />
        <JourneyCol vm={vms.vm2} cr={crs.cr1} crName="CR1" isAdvanced={isAdvanced} />
        <JourneyCol vm={vms.vm3} cr={crs.cr2} crName="CR2" isAdvanced={isAdvanced} />
        <JourneyCol vm={vms.vm5} cr={crs.cr4} crName="CR4" isAdvanced={isAdvanced} />
      </div>
    </section>
  )
}